import { a as loadCredentials, i as getToken } from "./credentials-CdxijtI9.js";
import { r as resolveCurrentOrg } from "./orgs-Dj0nij3E.js";
import { InvalidArgumentError } from "commander";
import color from "picocolors";
import { randomUUID } from "crypto";
import fs, { existsSync, mkdirSync, readFileSync, writeFileSync } from "fs";
import os from "os";
import path from "path";
import { PostHog } from "posthog-node";
import * as fs$1 from "fs/promises";
import fsPromises from "fs/promises";
import { fileURLToPath } from "url";
import { execa } from "execa";
import fsExtra from "fs-extra";
import fsExtra$1, { ensureFile, readJSON, writeJSON } from "fs-extra/esm";
import * as p from "@clack/prompts";
import yoctoSpinner from "yocto-spinner";
//#region src/commands/create/command.ts
const CREATE_LLM_PROVIDERS = [
	{
		value: "openai",
		label: "OpenAI"
	},
	{
		value: "anthropic",
		label: "Anthropic"
	},
	{
		value: "google",
		label: "Gemini"
	},
	{
		value: "xai",
		label: "xAI"
	}
];
function parseCreateLLMProvider(value) {
	if (!CREATE_LLM_PROVIDERS.some((provider) => provider.value === value)) throw new InvalidArgumentError(`Choose a valid provider: ${CREATE_LLM_PROVIDERS.map((provider) => provider.value).join(", ")}`);
	return value;
}
function parseCreateTimeout(value) {
	if (!/^\d+$/.test(value)) throw new InvalidArgumentError("Timeout must be a positive integer");
	const timeout = Number(value);
	if (!Number.isSafeInteger(timeout) || timeout <= 0) throw new InvalidArgumentError("Timeout must be a positive integer");
	return timeout;
}
function configureCreateCommand(command) {
	return command.description("Create a new Mastra project").argument("[project-name]", "Directory name of the project").option("--empty", "Create an empty project").option("-l, --llm <provider>", `Model provider (${CREATE_LLM_PROVIDERS.map((provider) => provider.value).join(", ")})`, parseCreateLLMProvider).option("-k, --llm-api-key <key>", "API key for the model provider").option("--no-skills", "Do not install Mastra skills").option("--no-git", "Do not initialize a git repository").option("--no-install", "Skip installing dependencies").option("-t, --template [template]", "Create from a template slug or public GitHub URL, or select interactively when omitted").option("--timeout <milliseconds>", "Package installation timeout in milliseconds", parseCreateTimeout, 6e4);
}
function normalizeCreateCommandOptions(projectName, options) {
	return {
		projectName,
		empty: options.empty ?? false,
		llmProvider: options.llm,
		llmApiKey: options.llmApiKey,
		skills: options.skills,
		git: options.git,
		template: options.template,
		timeout: options.timeout,
		install: options.install
	};
}
function getCreateMode(options) {
	if (options.empty) return "empty";
	if (options.template !== void 0) return "template";
	return "managed";
}
function validateCreateOptionConflicts(options) {
	if (options.empty && options.template !== void 0) throw new Error(`The --empty and --template options can't be used together`);
	const mode = getCreateMode(options);
	if (mode !== "managed" && options.llmProvider !== void 0) throw new Error("The --llm option can only be used with the default template");
	if (mode !== "managed" && options.llmApiKey !== void 0) throw new Error("The --llm-api-key option can only be used with the default template");
	return mode;
}
const WINDOWS_RESERVED_BASENAME = /^(?:con|prn|aux|nul|com[1-9]|lpt[1-9])(?:\..*)?$/i;
const PROJECT_NAME_PATTERN = /^[a-z0-9][a-z0-9._-]*$/;
function validateProjectName(value) {
	const projectName = value.trim();
	if (projectName.length < 1 || projectName.length > 214 || path.isAbsolute(projectName) || projectName.includes("/") || projectName.includes("\\") || projectName === "." || projectName === ".." || projectName.endsWith(".") || !PROJECT_NAME_PATTERN.test(projectName) || WINDOWS_RESERVED_BASENAME.test(projectName)) throw new Error("Project name must be 1-214 lowercase characters, start with a letter or number, and contain only letters, numbers, dots, hyphens, or underscores");
	return projectName;
}
const NUMERIC_IDENTIFIER_PATTERN = /^\d+$/;
function getPrereleaseChannel(version) {
	const separator = version.indexOf("-");
	if (separator === -1) return void 0;
	return version.slice(separator + 1).split(".").find((identifier) => !NUMERIC_IDENTIFIER_PATTERN.test(identifier));
}
function selectMatchingDistTag(version, output) {
	const matchingTags = output.split("\n").map((line) => line.trim()).filter(Boolean).flatMap((line) => {
		const separator = line.indexOf(":");
		if (separator === -1) return [];
		const tag = line.slice(0, separator).trim();
		return line.slice(separator + 1).trim() === version && tag ? [tag] : [];
	});
	if (matchingTags.length === 0) return void 0;
	const prereleaseChannel = getPrereleaseChannel(version);
	const matchingPrereleaseTag = prereleaseChannel ? matchingTags.find((tag) => prereleaseChannel === tag || prereleaseChannel.startsWith(`${tag}-`)) : void 0;
	if (matchingPrereleaseTag) return matchingPrereleaseTag;
	if (matchingTags.includes("latest")) return "latest";
	if (matchingTags.includes("beta")) return "beta";
	return matchingTags.sort((a, b) => a.localeCompare(b))[0];
}
//#endregion
//#region src/commands/init/mcp-docs-server-install.ts
const createArgs = (versionTag) => {
	return ["-y", versionTag ? `@mastra/mcp-docs-server@${versionTag}` : "@mastra/mcp-docs-server"];
};
const createMcpConfig = (editor, versionTag) => {
	const args = createArgs(versionTag);
	if (editor === "vscode") return { servers: { mastra: process.platform === `win32` ? {
		command: "cmd",
		args: [
			"/c",
			"npx",
			...args
		],
		type: "stdio"
	} : {
		command: "npx",
		args,
		type: "stdio"
	} } };
	return { mcpServers: { mastra: {
		command: "npx",
		args
	} } };
};
function makeConfig(original, editor, versionTag) {
	if (editor === "vscode") return {
		...original,
		servers: {
			...original?.servers || {},
			...createMcpConfig(editor, versionTag).servers
		}
	};
	return {
		...original,
		mcpServers: {
			...original?.mcpServers || {},
			...createMcpConfig(editor, versionTag).mcpServers
		}
	};
}
async function writeMergedConfig(configPath, editor, versionTag) {
	const config = makeConfig(existsSync(configPath) ? await readJSON(configPath) : {}, editor, versionTag);
	await ensureFile(configPath);
	await writeJSON(configPath, config, { spaces: 2 });
}
const windsurfGlobalMCPConfigPath = path.join(os.homedir(), ".codeium", "windsurf", "mcp_config.json");
const antigravityGlobalMCPConfigPath = path.join(os.homedir(), ".gemini", "antigravity", "mcp_config.json");
const cursorGlobalMCPConfigPath = path.join(os.homedir(), ".cursor", "mcp.json");
path.join(process.cwd(), ".vscode", "mcp.json");
const vscodeGlobalMCPConfigPath = path.join(os.homedir(), process.platform === "win32" ? path.join("AppData", "Roaming", "Code", "User", "settings.json") : process.platform === "darwin" ? path.join("Library", "Application Support", "Code", "User", "settings.json") : path.join(".config", "Code", "User", "settings.json"));
const EDITOR = [
	"cursor",
	"cursor-global",
	"windsurf",
	"vscode",
	"antigravity"
];
/**
* Type-guard to check if a string is a valid Editor
*/
function isValidEditor(value) {
	return EDITOR.includes(value);
}
async function installMastraDocsMCPServer({ editor, directory, versionTag }) {
	if (editor === `cursor`) await writeMergedConfig(path.join(directory, ".cursor", "mcp.json"), "cursor", versionTag);
	if (editor === `vscode`) await writeMergedConfig(path.join(directory, ".vscode", "mcp.json"), "vscode", versionTag);
	if (editor === `cursor-global`) {
		if (await globalMCPIsAlreadyInstalled(editor, versionTag)) return;
		await writeMergedConfig(cursorGlobalMCPConfigPath, "cursor-global", versionTag);
	}
	if (editor === `windsurf`) {
		if (await globalMCPIsAlreadyInstalled(editor, versionTag)) return;
		await writeMergedConfig(windsurfGlobalMCPConfigPath, editor, versionTag);
	}
	if (editor === `antigravity`) {
		if (await globalMCPIsAlreadyInstalled(editor, versionTag)) return;
		await writeMergedConfig(antigravityGlobalMCPConfigPath, editor, versionTag);
	}
}
async function globalMCPIsAlreadyInstalled(editor, versionTag) {
	let configPath = ``;
	if (editor === "windsurf") configPath = windsurfGlobalMCPConfigPath;
	else if (editor === "antigravity") configPath = antigravityGlobalMCPConfigPath;
	else if (editor === "cursor-global") configPath = cursorGlobalMCPConfigPath;
	else if (editor === "vscode") configPath = vscodeGlobalMCPConfigPath;
	if (!configPath || !existsSync(configPath)) return false;
	try {
		const configContents = await readJSON(configPath);
		if (!configContents) return false;
		const expectedPackage = versionTag ? `@mastra/mcp-docs-server@${versionTag}` : "@mastra/mcp-docs-server";
		if (editor === "vscode") {
			if (!configContents.servers) return false;
			return Object.values(configContents.servers).some((server) => server?.args?.find((arg) => arg === expectedPackage));
		}
		if (!configContents?.mcpServers) return false;
		return Object.values(configContents.mcpServers).some((server) => server?.args?.find((arg) => arg === expectedPackage));
	} catch {
		return false;
	}
}
//#endregion
//#region src/utils/package-manager.ts
function getPackageManagerAddCommand(pm) {
	switch (pm) {
		case "npm": return "install --audit=false --fund=false --loglevel=error --progress=false --update-notifier=false";
		case "yarn": return "add";
		case "pnpm": return "add --loglevel=error";
		case "bun": return "add";
		default: return "add";
	}
}
//#endregion
//#region src/services/service.deps.ts
var DepsService = class {
	packageManager;
	constructor() {
		this.packageManager = this.getPackageManager();
	}
	findLockFile(dir) {
		for (const file of [
			"pnpm-lock.yaml",
			"package-lock.json",
			"yarn.lock",
			"bun.lock",
			"bun.lockb"
		]) if (fs.existsSync(path.join(dir, file))) return file;
		const parentDir = path.resolve(dir, "..");
		if (parentDir !== dir) return this.findLockFile(parentDir);
		return null;
	}
	getPackageManager() {
		switch (this.findLockFile(process.cwd())) {
			case "pnpm-lock.yaml": return "pnpm";
			case "package-lock.json": return "npm";
			case "yarn.lock": return "yarn";
			case "bun.lock":
			case "bun.lockb": return "bun";
			default: return "npm";
		}
	}
	async installPackages(packages) {
		const pm = this.packageManager;
		return execa(pm, [...getPackageManagerAddCommand(pm).split(" "), ...packages], {
			all: true,
			stdio: "pipe"
		});
	}
	async checkDependencies(dependencies) {
		try {
			const packageJsonPath = path.join(process.cwd(), "package.json");
			try {
				await fsPromises.access(packageJsonPath);
			} catch {
				return "No package.json file found in the current directory";
			}
			const packageJson = JSON.parse(await fsPromises.readFile(packageJsonPath, "utf-8"));
			for (const dependency of dependencies) if (!packageJson.dependencies || !packageJson.dependencies[dependency]) return `Please install ${dependency} before running this command (${this.packageManager} install ${dependency})`;
			return "ok";
		} catch (err) {
			console.error(err);
			return "Could not check dependencies";
		}
	}
	async getProjectName() {
		try {
			const packageJsonPath = path.join(process.cwd(), "package.json");
			const packageJson = await fsPromises.readFile(packageJsonPath, "utf-8");
			return JSON.parse(packageJson).name;
		} catch (err) {
			throw err;
		}
	}
	async addScriptsToPackageJson(scripts) {
		const packageJson = JSON.parse(await fsPromises.readFile("package.json", "utf-8"));
		packageJson.scripts = {
			...packageJson.scripts,
			...scripts
		};
		await fsPromises.writeFile("package.json", JSON.stringify(packageJson, null, 2));
	}
};
//#endregion
//#region src/services/service.env.ts
var EnvService = class {};
function escapeRegExp(value) {
	return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
var FileEnvService = class FileEnvService extends EnvService {
	static ENV_KEY_REGEX = /^[A-Za-z_][A-Za-z0-9_]*$/;
	filePath;
	validateEnvEntry(key, value) {
		if (!FileEnvService.ENV_KEY_REGEX.test(key)) throw new Error(`Invalid ENV key: ${key}`);
		if (/[\r\n]/.test(value)) throw new Error(`Invalid ENV value for ${key}: multiline values are not supported.`);
	}
	constructor(filePath) {
		super();
		this.filePath = filePath;
	}
	envLineRegex(key, captureValue = false) {
		const pattern = captureValue ? `^${escapeRegExp(key)}=(.*)$` : `^${escapeRegExp(key)}=.*$`;
		return new RegExp(pattern, "m");
	}
	async updateEnvData({ key, value, filePath = this.filePath, data }) {
		this.validateEnvEntry(key, value);
		const lineRegex = this.envLineRegex(key);
		const updated = lineRegex.test(data) ? data.replace(lineRegex, () => `${key}=${value}`) : `${data}\n${key}=${value}`;
		await fs$1.writeFile(filePath, updated, "utf8");
		console.info(`${key} set in ENV file.`);
		return updated;
	}
	async getEnvValue(key) {
		try {
			return (await fs$1.readFile(this.filePath, "utf8")).match(this.envLineRegex(key, true))?.[1] ?? null;
		} catch (err) {
			console.error(`Error reading ENV value: ${err}`);
			return null;
		}
	}
	async setEnvValue(key, value) {
		try {
			const data = await fs$1.readFile(this.filePath, "utf8");
			await this.updateEnvData({
				key,
				value,
				data
			});
		} catch (err) {
			console.error(`Error writing ENV value: ${err}`);
		}
	}
};
//#endregion
//#region src/services/service.file.ts
var FileService = class {
	/**
	*
	* @param inputFile the file in the starter files directory to copy
	* @param outputFilePath the destination path
	* @param replaceIfExists flag to replace if it exists
	* @returns
	*/
	async copyStarterFile(inputFile, outputFilePath, replaceIfExists) {
		const __filename = fileURLToPath(import.meta.url);
		const __dirname = path.dirname(__filename);
		const filePath = path.resolve(__dirname, "starter-files", inputFile);
		const fileString = fs.readFileSync(filePath, "utf8");
		if (fs.existsSync(outputFilePath) && !replaceIfExists) {
			console.info(`${outputFilePath} already exists`);
			return false;
		}
		await fsExtra$1.outputFile(outputFilePath, fileString);
		return true;
	}
	async setupEnvFile({ dbUrl }) {
		const envPath = path.join(process.cwd(), ".env.development");
		await fsExtra$1.ensureFile(envPath);
		await new FileEnvService(envPath).setEnvValue("DB_URL", dbUrl);
	}
	getFirstExistingFile(files) {
		for (const f of files) if (fs.existsSync(f)) return f;
		throw new Error("Missing required file, checked the following paths: " + files.join(", "));
	}
	replaceValuesInFile({ filePath, replacements }) {
		let fileContent = fs.readFileSync(filePath, "utf8");
		replacements.forEach(({ search, replace }) => {
			fileContent = fileContent.replaceAll(search, replace);
		});
		fs.writeFileSync(filePath, fileContent);
	}
};
//#endregion
//#region src/commands/init/utils.ts
const LLMProvider = [
	"openai",
	"anthropic",
	"groq",
	"google",
	"cerebras",
	"mistral"
];
const COMPONENTS = [
	"agents",
	"workflows",
	"tools",
	"scorers"
];
async function promptForObservability(command, onObservabilitySelected) {
	while (true) {
		const choice = await p.select({
			message: "Enable Mastra Observability? (will open auth flow)",
			options: [{
				value: "yes",
				label: "Yes"
			}, {
				value: "no",
				label: "No"
			}],
			initialValue: "yes",
			showInstructions: false
		});
		if (p.isCancel(choice)) return {};
		const answer = choice === "yes" ? "yes" : "no";
		const enabled = answer === "yes";
		onObservabilitySelected?.({
			command,
			enabled,
			answer,
			selection_method: "interactive"
		});
		if (!enabled) return { enabled: false };
		const hadCachedCreds = await loadCredentials() !== null;
		try {
			const token = await getToken();
			if (hadCachedCreds) {
				const creds = await loadCredentials();
				if (creds) p.log.info(`Logged in as ${creds.user.email}`);
			}
			const org = await resolveCurrentOrg(token, { forcePrompt: true });
			return {
				enabled: true,
				token,
				orgId: org.orgId,
				orgName: org.orgName
			};
		} catch (err) {
			const message = err instanceof Error ? err.message : String(err);
			p.log.warn(`Could not sign in to Mastra: ${message}`);
		}
	}
}
/**
* Type-guard to check if a value is a valid LLMProvider
*/
function isValidLLMProvider(value) {
	return LLMProvider.includes(value);
}
/**
* Type-guard to check if a value contains only valid Components
*/
function areValidComponents(values) {
	return values.every((value) => COMPONENTS.includes(value));
}
const getModelIdentifier = (llmProvider) => {
	let model = "openai/gpt-5-mini";
	if (llmProvider === "anthropic") model = "anthropic/claude-sonnet-4-5";
	else if (llmProvider === "groq") model = "groq/llama-3.3-70b-versatile";
	else if (llmProvider === "google") model = "google/gemini-2.5-pro";
	else if (llmProvider === "cerebras") model = "cerebras/llama-3.3-70b";
	else if (llmProvider === "mistral") model = "mistral/mistral-medium-2508";
	return model;
};
async function writeAgentSample(llmProvider, destPath, addExampleTool, addScorers) {
	const modelString = getModelIdentifier(llmProvider);
	const instructions = `You are a helpful weather assistant that provides accurate weather information and can help planning activities based on the weather.

Your primary function is to help users get weather details for specific locations. When responding:
- Always ask for a location if none is provided
- If the location name isn't in English, please translate it
- If giving a location with multiple parts (e.g. "New York, NY"), use the most relevant part (e.g. "New York")
- Include relevant details like humidity, wind conditions, and precipitation
- Keep responses concise but informative
- If the user asks for activities and provides the weather forecast, suggest activities based on the weather forecast.
- If the user asks for activities, respond in the format they request.${addExampleTool ? "\n\nUse the weatherTool to fetch current weather data." : ""}`;
	const content = `${[
		`import { Agent } from '@mastra/core/agent';`,
		`import { Memory } from '@mastra/memory';`,
		addExampleTool ? `import { weatherTool } from '../tools/weather-tool';` : void 0,
		addScorers ? `import { scorers } from '../scorers/weather-scorer';` : void 0
	].filter(Boolean).join("\n")}

export const weatherAgent = new Agent({
  id: 'weather-agent',
  name: 'Weather Agent',
  instructions: \`${instructions}\`,
  model: '${modelString}',
${addExampleTool ? `  tools: { weatherTool },\n` : ""}${addScorers ? `  scorers: {
    toolCallAppropriateness: {
      scorer: scorers.toolCallAppropriatenessScorer,
      sampling: {
        type: 'ratio',
        rate: 1,
      },
    },
    completeness: {
      scorer: scorers.completenessScorer,
      sampling: {
        type: 'ratio',
        rate: 1,
      },
    },
    translation: {
      scorer: scorers.translationScorer,
      sampling: {
        type: 'ratio',
        rate: 1,
      },
    },
  },
` : ""}  memory: new Memory(),
});
`;
	await fsPromises.writeFile(destPath, content);
}
async function writeWorkflowSample(destPath) {
	await fsPromises.writeFile(destPath, `import { createStep, createWorkflow } from '@mastra/core/workflows';
import { z } from 'zod';

const forecastSchema = z.object({
  date: z.string(),
  maxTemp: z.number(),
  minTemp: z.number(),
  precipitationChance: z.number(),
  condition: z.string(),
  location: z.string(),
})

function getWeatherCondition(code: number): string {
  const conditions: Record<number, string> = {
    0: 'Clear sky',
    1: 'Mainly clear',
    2: 'Partly cloudy',
    3: 'Overcast',
    45: 'Foggy',
    48: 'Depositing rime fog',
    51: 'Light drizzle',
    53: 'Moderate drizzle',
    55: 'Dense drizzle',
    61: 'Slight rain',
    63: 'Moderate rain',
    65: 'Heavy rain',
    71: 'Slight snow fall',
    73: 'Moderate snow fall',
    75: 'Heavy snow fall',
    95: 'Thunderstorm',
  }
  return conditions[code] || 'Unknown'
}

const fetchWeather = createStep({
  id: 'fetch-weather',
  description: 'Fetches weather forecast for a given city',
  inputSchema: z.object({
    city: z.string().describe('The city to get the weather for'),
  }),
  outputSchema: forecastSchema,
  execute: async ({ inputData }) => {
    if (!inputData) {
      throw new Error('Input data not found');
    }

    const geocodingUrl = \`https://geocoding-api.open-meteo.com/v1/search?name=\${encodeURIComponent(inputData.city)}&count=1\`;
    const geocodingResponse = await fetch(geocodingUrl);
    const geocodingData = (await geocodingResponse.json()) as {
      results: { latitude: number; longitude: number; name: string }[];
    };

    if (!geocodingData.results?.[0]) {
      throw new Error(\`Location '\${inputData.city}' not found\`);
    }

    const { latitude, longitude, name } = geocodingData.results[0];

    const weatherUrl = \`https://api.open-meteo.com/v1/forecast?latitude=\${latitude}&longitude=\${longitude}&current=precipitation,weathercode&timezone=auto,&hourly=precipitation_probability,temperature_2m\`;
    const response = await fetch(weatherUrl);
    const data = (await response.json()) as {
      current: {
        time: string
        precipitation: number
        weathercode: number
      }
      hourly: {
        precipitation_probability: number[]
        temperature_2m: number[]
      }
    }

    const forecast = {
      date: new Date().toISOString(),
      maxTemp: Math.max(...data.hourly.temperature_2m),
      minTemp: Math.min(...data.hourly.temperature_2m),
      condition: getWeatherCondition(data.current.weathercode),
      precipitationChance: data.hourly.precipitation_probability.reduce(
        (acc, curr) => Math.max(acc, curr),
        0
      ),
      location: name
    }

    return forecast;
  },
});


const planActivities = createStep({
  id: 'plan-activities',
  description: 'Suggests activities based on weather conditions',
  inputSchema: forecastSchema,
  outputSchema: z.object({
    activities: z.string(),
  }),
  execute: async ({ inputData, mastra }) => {
    const forecast = inputData

    if (!forecast) {
      throw new Error('Forecast data not found')
    }

    const agent = mastra?.getAgent('weatherAgent');
    if (!agent) {
      throw new Error('Weather agent not found');
    }

    const prompt = \`Based on the following weather forecast for \${forecast.location}, suggest appropriate activities:
      \${JSON.stringify(forecast, null, 2)}
      For each day in the forecast, structure your response exactly as follows:

      📅 [Day, Month Date, Year]
      ═══════════════════════════

      🌡️ WEATHER SUMMARY
      • Conditions: [brief description]
      • Temperature: [X°C/Y°F to A°C/B°F]
      • Precipitation: [X% chance]

      🌅 MORNING ACTIVITIES
      Outdoor:
      • [Activity Name] - [Brief description including specific location/route]
        Best timing: [specific time range]
        Note: [relevant weather consideration]

      🌞 AFTERNOON ACTIVITIES
      Outdoor:
      • [Activity Name] - [Brief description including specific location/route]
        Best timing: [specific time range]
        Note: [relevant weather consideration]

      🏠 INDOOR ALTERNATIVES
      • [Activity Name] - [Brief description including specific venue]
        Ideal for: [weather condition that would trigger this alternative]

      ⚠️ SPECIAL CONSIDERATIONS
      • [Any relevant weather warnings, UV index, wind conditions, etc.]

      Guidelines:
      - Suggest 2-3 time-specific outdoor activities per day
      - Include 1-2 indoor backup options
      - For precipitation >50%, lead with indoor activities
      - All activities must be specific to the location
      - Include specific venues, trails, or locations
      - Consider activity intensity based on temperature
      - Keep descriptions concise but informative

      Maintain this exact formatting for consistency, using the emoji and section headers as shown.\`;

    const response = await agent.stream([
      {
        role: 'user',
        content: prompt,
      },
    ]);

    let activitiesText = '';

    for await (const chunk of response.textStream) {
      process.stdout.write(chunk);
      activitiesText += chunk;
    }

    return {
      activities: activitiesText,
    };
  },
});

const weatherWorkflow = createWorkflow({
  id: 'weather-workflow',
  inputSchema: z.object({
    city: z.string().describe('The city to get the weather for'),
  }),
  outputSchema: z.object({
    activities: z.string(),
  })
})
  .then(fetchWeather)
  .then(planActivities);

weatherWorkflow.commit();

export { weatherWorkflow };`);
}
async function writeToolSample(destPath) {
	await new FileService().copyStarterFile("tools.ts", destPath);
}
async function writeScorersSample(llmProvider, destPath) {
	const content = `import { z } from 'zod';
import { createToolCallAccuracyScorerCode } from '@mastra/evals/scorers/prebuilt';
import { createCompletenessScorer } from '@mastra/evals/scorers/prebuilt';
import { getAssistantMessageFromRunOutput, getUserMessageFromRunInput } from '@mastra/evals/scorers/utils';
import { createScorer } from '@mastra/core/evals';

export const toolCallAppropriatenessScorer = createToolCallAccuracyScorerCode({
  expectedTool: 'weatherTool',
  strictMode: false,
});

export const completenessScorer = createCompletenessScorer();

// Custom LLM-judged scorer: evaluates if non-English locations are translated appropriately
export const translationScorer = createScorer({
  id: 'translation-quality-scorer',
  name: 'Translation Quality',
  description: 'Checks that non-English location names are translated and used correctly',
  type: 'agent',
  judge: {
    model: '${getModelIdentifier(llmProvider)}',
    instructions:
      'You are an expert evaluator of translation quality for geographic locations. ' +
      'Determine whether the user text mentions a non-English location and whether the assistant correctly uses an English translation of that location. ' +
      'Be lenient with transliteration differences and diacritics. ' +
      'Return only the structured JSON matching the provided schema.',
  },
})
  .preprocess(({ run }) => {
    const userText = getUserMessageFromRunInput(run.input) || '';
    const assistantText = getAssistantMessageFromRunOutput(run.output) || '';
    return { userText, assistantText };
  })
  .analyze({
    description: 'Extract location names and detect language/translation adequacy',
    outputSchema: z.object({
      nonEnglish: z.boolean(),
      translated: z.boolean(),
      confidence: z.number().min(0).max(1).default(1),
      explanation: z.string().default(''),
    }),
    createPrompt: ({ results }) => \`
            You are evaluating if a weather assistant correctly handled translation of a non-English location.
            User text:
            """
            \${results.preprocessStepResult.userText}
            """
            Assistant response:
            """
            \${results.preprocessStepResult.assistantText}
            """
            Tasks:
            1) Identify if the user mentioned a location that appears non-English.
            2) If non-English, check whether the assistant used a correct English translation of that location in its response.
            3) Be lenient with transliteration differences (e.g., accents/diacritics).
            Return JSON with fields:
            {
            "nonEnglish": boolean,
            "translated": boolean,
            "confidence": number, // 0-1
            "explanation": string
            }
        \`,
  })
  .generateScore(({ results }) => {
    const r = (results as any)?.analyzeStepResult || {};
    if (!r.nonEnglish) return 1; // If not applicable, full credit
    if (r.translated) return Math.max(0, Math.min(1, 0.7 + 0.3 * (r.confidence ?? 1)));
    return 0; // Non-English but not translated
  })
  .generateReason(({ results, score }) => {
    const r = (results as any)?.analyzeStepResult || {};
    return \`Translation scoring: nonEnglish=\${r.nonEnglish ?? false}, translated=\${r.translated ?? false}, confidence=\${r.confidence ?? 0}. Score=\${score}. \${r.explanation ?? ''}\`;
  });

export const scorers = {
  toolCallAppropriatenessScorer,
  completenessScorer,
  translationScorer,
};`;
	await fsPromises.writeFile(destPath, content);
}
async function writeCodeSampleForComponents(llmprovider, component, destPath, importComponents) {
	switch (component) {
		case "agents": return writeAgentSample(llmprovider, destPath, importComponents.includes("tools"), importComponents.includes("scorers"));
		case "tools": return writeToolSample(destPath);
		case "workflows": return writeWorkflowSample(destPath);
		case "scorers": return writeScorersSample(llmprovider, destPath);
		default: return "";
	}
}
const createComponentsDir = async (dirPath, component) => {
	const componentPath = dirPath + `/${component}`;
	await fsExtra$1.ensureDir(componentPath);
};
const writeIndexFile = async ({ dirPath, addAgent, addExample, addWorkflow, addScorers }) => {
	const indexPath = dirPath + "/index.ts";
	const destPath = path.join(indexPath);
	try {
		await fsPromises.writeFile(destPath, "");
		const filteredExports = [
			addWorkflow ? `workflows: { weatherWorkflow },` : "",
			addAgent ? `agents: { weatherAgent },` : "",
			addScorers ? `scorers: { toolCallAppropriatenessScorer, completenessScorer, translationScorer },` : ""
		].filter(Boolean);
		if (!addExample) {
			await fsPromises.writeFile(destPath, `
import { Mastra } from '@mastra/core/mastra';

export const mastra = new Mastra()
        `);
			return;
		}
		await fsPromises.writeFile(destPath, `
import { Mastra } from '@mastra/core/mastra';
import { PinoLogger } from '@mastra/loggers';
import { LibSQLStore } from '@mastra/libsql';
import { DuckDBStore } from "@mastra/duckdb";
import { MastraCompositeStore } from '@mastra/core/storage';
import { Observability, MastraStorageExporter, MastraPlatformExporter, SensitiveDataFilter } from '@mastra/observability';
${addWorkflow ? `import { weatherWorkflow } from './workflows/weather-workflow';` : ""}
${addAgent ? `import { weatherAgent } from './agents/weather-agent';` : ""}
${addScorers ? `import { toolCallAppropriatenessScorer, completenessScorer, translationScorer } from './scorers/weather-scorer';` : ""}

export const mastra = new Mastra({
  ${filteredExports.join("\n  ")}
  storage: new MastraCompositeStore({
    id: 'composite-storage',
    default: new LibSQLStore({
      id: "mastra-storage",
      // Uses a hosted database when deployed (mastra env db create --kind turso),
      // and a local file during development.
      url: process.env.TURSO_DATABASE_URL ?? "file:./mastra.db",
      authToken: process.env.TURSO_AUTH_TOKEN,
    }),
    domains: {
      observability: await new DuckDBStore().getStore('observability'),
    }
  }),
  logger: new PinoLogger({
    name: 'Mastra',
    level: 'info',
  }),
  observability: new Observability({
    configs: {
      default: {
        serviceName: 'mastra',
        exporters: [
          new MastraStorageExporter(), // Persists observability events to Mastra Storage
          new MastraPlatformExporter(), // Sends observability events to Mastra Platform (if MASTRA_PLATFORM_ACCESS_TOKEN is set)
        ],
        spanOutputProcessors: [
          new SensitiveDataFilter(), // Redacts sensitive data like passwords, tokens, keys
        ],
      },
    },
  }),
});
`);
	} catch (err) {
		throw err;
	}
};
const checkAndInstallCoreDeps = async (addExample, versionTag) => {
	const spinner = yoctoSpinner({ text: "Installing Mastra core dependencies" });
	let packages = [];
	const mastraVersionTag = versionTag || "latest";
	try {
		const depService = new DepsService();
		spinner.start();
		const needsCore = await depService.checkDependencies(["@mastra/core"]) !== `ok`;
		const needsCli = await depService.checkDependencies(["mastra"]) !== `ok`;
		const needsZod = await depService.checkDependencies(["zod"]) !== `ok`;
		if (needsCore) packages.push({
			name: "@mastra/core",
			version: mastraVersionTag
		});
		if (needsCli) packages.push({
			name: "mastra",
			version: mastraVersionTag
		});
		if (needsZod) packages.push({
			name: "zod",
			version: "^4"
		});
		if (addExample) {
			if (await depService.checkDependencies(["@mastra/libsql"]) !== `ok`) packages.push({
				name: "@mastra/libsql",
				version: mastraVersionTag
			});
		}
		if (packages.length > 0) await depService.installPackages(packages.map((pkg) => `${pkg.name}@${pkg.version}`));
		spinner.success("Successfully installed Mastra core dependencies");
	} catch (err) {
		spinner.error(`Failed to install core dependencies: ${err instanceof Error ? err.message : "Unknown error"}`);
	}
};
const getAPIKey = async (provider) => {
	let key = "OPENAI_API_KEY";
	switch (provider) {
		case "anthropic":
			key = "ANTHROPIC_API_KEY";
			return key;
		case "groq":
			key = "GROQ_API_KEY";
			return key;
		case "google":
			key = "GOOGLE_API_KEY";
			return key;
		case "cerebras":
			key = "CEREBRAS_API_KEY";
			return key;
		case "mistral":
			key = "MISTRAL_API_KEY";
			return key;
		default: return key;
	}
};
const writeAPIKey = async ({ provider, apiKey }) => {
	/**
	* If people skip entering an API key (because they e.g. have it in their environment already), we write to .env.example instead of .env so that they can immediately run Mastra without having to delete an .env file with an invalid key.
	*/
	const envFileName = apiKey ? ".env" : ".env.example";
	const key = await getAPIKey(provider);
	await fsPromises.appendFile(envFileName, `${key}=${apiKey || "your-api-key"}\n`, apiKey ? {
		encoding: "utf8",
		mode: 384
	} : "utf8");
	if (apiKey && process.platform !== "win32") await fsPromises.chmod(envFileName, 384);
};
/**
* Append Mastra platform credentials to the project's `.env` file.
*
* The generated `src/mastra/index.ts` template already registers a
* `MastraPlatformExporter` which no-ops unless `MASTRA_PLATFORM_ACCESS_TOKEN`
* is set, so enabling platform is a pure env-var concern from the
* scaffolder's side.
*
* When called with no token, writes empty placeholders so the user can paste
* a key minted manually from the dashboard.
*/
const MASTRA_PLATFORM_ENV_KEYS = [
	"MASTRA_PLATFORM_ACCESS_TOKEN",
	"MASTRA_PROJECT_ID",
	"MASTRA_PLATFORM_OBSERVABILITY_ENDPOINT"
];
function upsertEnvValue(content, key, value) {
	const lines = content.split(/\r?\n/);
	const assignment = `${key}=${value}`;
	let updated = false;
	const nextLines = lines.filter((line) => {
		if (!line.startsWith(`${key}=`)) return true;
		if (updated) return false;
		updated = true;
		return true;
	});
	if (updated) {
		const index = nextLines.findIndex((line) => line.startsWith(`${key}=`));
		nextLines[index] = assignment;
	} else {
		if (nextLines.at(-1) === "") nextLines.pop();
		nextLines.push(assignment, "");
	}
	return nextLines.join("\n");
}
const writeObservabilityEnv = async ({ projectPath, token, projectId, endpoint } = {}) => {
	const envFilePath = path.join(projectPath ?? process.cwd(), ".env");
	let content = "";
	try {
		content = await fsPromises.readFile(envFilePath, "utf8");
	} catch (error) {
		if (error.code !== "ENOENT") throw error;
	}
	if (!MASTRA_PLATFORM_ENV_KEYS.some((key) => content.split(/\r?\n/).some((line) => line.startsWith(`${key}=`)))) {
		if (content && !content.endsWith("\n")) content += "\n";
		content += [
			"",
			"# Mastra platform — https://projects.mastra.ai",
			"# Access token and project id wired up automatically when you ran",
			"# `mastra init` / `create-mastra` with Mastra platform enabled.",
			`MASTRA_PLATFORM_ACCESS_TOKEN=${token ?? ""}`,
			`MASTRA_PROJECT_ID=${projectId ?? ""}`,
			...endpoint ? [`MASTRA_PLATFORM_OBSERVABILITY_ENDPOINT=${endpoint}`] : [],
			""
		].join("\n");
	} else {
		content = upsertEnvValue(content, "MASTRA_PLATFORM_ACCESS_TOKEN", token ?? "");
		content = upsertEnvValue(content, "MASTRA_PROJECT_ID", projectId ?? "");
		if (endpoint) content = upsertEnvValue(content, "MASTRA_PLATFORM_OBSERVABILITY_ENDPOINT", endpoint);
	}
	await fsPromises.writeFile(envFilePath, content, {
		encoding: "utf8",
		mode: 384
	});
	if (process.platform !== "win32") await fsPromises.chmod(envFilePath, 384);
};
const createMastraDir = async (directory) => {
	let dir = directory.trim().split("/").filter((item) => item !== "");
	const dirPath = path.join(process.cwd(), ...dir, "mastra");
	try {
		await fsPromises.access(dirPath);
		return { ok: false };
	} catch {
		await fsExtra$1.ensureDir(dirPath);
		return {
			ok: true,
			dirPath
		};
	}
};
const writeCodeSample = async (dirPath, component, llmProvider, importComponents) => {
	const destPath = dirPath + `/${component}/weather-${component.slice(0, -1)}.ts`;
	try {
		await writeCodeSampleForComponents(llmProvider, component, destPath, importComponents);
	} catch (err) {
		throw err;
	}
};
const LLM_PROVIDERS = [
	{
		value: "openai",
		label: "OpenAI",
		hint: "recommended"
	},
	{
		value: "anthropic",
		label: "Anthropic"
	},
	{
		value: "groq",
		label: "Groq"
	},
	{
		value: "google",
		label: "Google"
	},
	{
		value: "cerebras",
		label: "Cerebras"
	},
	{
		value: "mistral",
		label: "Mistral"
	}
];
const interactivePrompt = async (args = {}) => {
	const { skip = {}, options: { command, showBanner = true, onObservabilitySelected } = {} } = args;
	if (showBanner) p.intro(color.inverse(" Mastra Init "));
	const { configureMastraToolingForAgents, observability, ...rest } = await p.group({
		directory: () => skip?.directory ? void 0 : p.text({
			message: "Where should we create the Mastra files? (default: src/)",
			placeholder: "src/",
			defaultValue: "src/"
		}),
		llmProvider: () => skip?.llmProvider ? void 0 : p.select({
			message: "Select a default model provider:",
			options: LLM_PROVIDERS,
			showInstructions: false
		}),
		llmApiKey: async ({ results: { llmProvider } }) => {
			if (skip?.llmApiKey) return void 0;
			const llmName = LLM_PROVIDERS.find((p) => p.value === llmProvider)?.label || "provider";
			if (await p.select({
				message: `Enter your ${llmName} API key?`,
				options: [{
					value: "skip",
					label: "Skip for now",
					hint: "default"
				}, {
					value: "enter",
					label: "Enter API key"
				}],
				initialValue: "skip",
				showInstructions: false
			}) === "enter") return p.password({
				message: "Enter your API key:",
				mask: "*",
				clearOnError: true,
				validate: (value) => {
					if (!value || value.length === 0) return "API key cannot be empty";
				}
			});
		},
		observability: async () => {
			if (skip?.observability) return void 0;
			return promptForObservability(command, onObservabilitySelected);
		},
		configureMastraToolingForAgents: async () => {
			if (skip?.skills && skip?.mcpServer) return {
				skills: void 0,
				mcpServer: void 0
			};
			const choice = await p.select({
				message: `Select tooling for your coding assistant:`,
				options: [{
					value: "skills",
					label: "Skills",
					hint: "recommended"
				}, {
					value: "mcp",
					label: "MCP Docs Server"
				}],
				initialValue: "skills",
				showInstructions: false
			});
			if (p.isCancel(choice)) return {
				skills: void 0,
				mcpServer: void 0
			};
			if (choice === "skills") {
				const POPULAR_AGENTS = [{
					value: "universal",
					label: "Universal (Codex, Cursor, Gemini, GitHub, OpenCode)"
				}, {
					value: "claude-code",
					label: "Claude Code"
				}];
				const ALL_AGENTS = [
					...POPULAR_AGENTS,
					{
						value: "adal",
						label: "AdaL"
					},
					{
						value: "antigravity",
						label: "Antigravity"
					},
					{
						value: "augment",
						label: "Augment"
					},
					{
						value: "codebuddy",
						label: "CodeBuddy"
					},
					{
						value: "command-code",
						label: "Command Code"
					},
					{
						value: "crush",
						label: "Crush"
					},
					{
						value: "droid",
						label: "Droid"
					},
					{
						value: "goose",
						label: "Goose"
					},
					{
						value: "iflow-cli",
						label: "iFlow CLI"
					},
					{
						value: "junie",
						label: "Junie"
					},
					{
						value: "kilo",
						label: "Kilo Code"
					},
					{
						value: "kiro-cli",
						label: "Kiro CLI"
					},
					{
						value: "kode",
						label: "Kode"
					},
					{
						value: "mcpjam",
						label: "MCPJam"
					},
					{
						value: "mistral-vibe",
						label: "Mistral Vibe"
					},
					{
						value: "mux",
						label: "Mux"
					},
					{
						value: "neovate",
						label: "Neovate"
					},
					{
						value: "openclaude",
						label: "OpenClaude IDE"
					},
					{
						value: "openclaw",
						label: "OpenClaw"
					},
					{
						value: "openhands",
						label: "OpenHands"
					},
					{
						value: "pi",
						label: "Pi"
					},
					{
						value: "pochi",
						label: "Pochi"
					},
					{
						value: "qoder",
						label: "Qoder"
					},
					{
						value: "qwen-code",
						label: "Qwen Code"
					},
					{
						value: "replit",
						label: "Replit"
					},
					{
						value: "roo",
						label: "Roo Code"
					},
					{
						value: "trae",
						label: "Trae"
					},
					{
						value: "trae-cn",
						label: "Trae CN"
					},
					{
						value: "windsurf",
						label: "Windsurf"
					},
					{
						value: "zencoder",
						label: "Zencoder"
					}
				];
				const initialSelection = await p.select({
					message: `Select your coding assistant:`,
					options: [...POPULAR_AGENTS, {
						value: "__show_all__",
						label: "+ Show all agents"
					}],
					initialValue: "universal"
				});
				if (p.isCancel(initialSelection)) return {
					skills: void 0,
					mcpServer: void 0
				};
				let selectedAgents = /* @__PURE__ */ new Set();
				if (initialSelection === "__show_all__") {
					const followUpSelection = await p.select({
						message: `Select your coding assistant:`,
						options: ALL_AGENTS
					});
					if (p.isCancel(followUpSelection)) return {
						skills: void 0,
						mcpServer: void 0
					};
					selectedAgents.add(followUpSelection);
				} else selectedAgents.add(initialSelection);
				selectedAgents.add("universal");
				return {
					skills: Array.from(selectedAgents),
					mcpServer: void 0
				};
			}
			if (choice === "mcp") {
				const editor = await p.select({
					message: `Select your coding assistant:`,
					options: [
						{
							value: "cursor",
							label: "Cursor (project only)"
						},
						{
							value: "cursor-global",
							label: "Cursor (global, all projects)"
						},
						{
							value: "windsurf",
							label: "Windsurf"
						},
						{
							value: "vscode",
							label: "VSCode"
						},
						{
							value: "antigravity",
							label: "Antigravity"
						}
					]
				});
				if (p.isCancel(editor)) return {
					skills: void 0,
					mcpServer: void 0
				};
				if (editor === `cursor`) p.log.message(`\nNote: you will need to go into Cursor Settings -> MCP Settings and manually enable the installed Mastra MCP server.\n`);
				if (editor === `cursor-global`) {
					if (await p.select({
						message: `Global install will add/update ${cursorGlobalMCPConfigPath} and make the Mastra docs MCP server available in all your Cursor projects. Continue?`,
						options: [{
							value: "yes",
							label: "Yes, I understand"
						}, {
							value: "no",
							label: "No, cancel"
						}]
					}) !== `yes`) return {
						skills: void 0,
						mcpServer: void 0
					};
				}
				if (editor === `windsurf`) {
					if (await p.select({
						message: `Windsurf only supports a global MCP config (at ${windsurfGlobalMCPConfigPath}) is it ok to add/update that global config?\nThis means the Mastra docs MCP server will be available in all your Windsurf projects.`,
						options: [{
							value: "yes",
							label: "Yes, I understand"
						}, {
							value: "no",
							label: "No, cancel"
						}]
					}) !== `yes`) return {
						skills: void 0,
						mcpServer: void 0
					};
				}
				if (editor === `antigravity`) {
					if (await p.select({
						message: `Antigravity only supports a global MCP config (at ${antigravityGlobalMCPConfigPath}). Is it ok to add/update that global config?\nThis will make the Mastra docs MCP server available in all Antigravity projects.`,
						options: [{
							value: "yes",
							label: "Yes, I understand"
						}, {
							value: "no",
							label: "No, cancel"
						}]
					}) !== `yes`) return {
						skills: void 0,
						mcpServer: void 0
					};
				}
				return {
					skills: void 0,
					mcpServer: editor
				};
			}
			return {
				skills: void 0,
				mcpServer: void 0
			};
		},
		initGit: async () => {
			if (skip?.gitInit) return false;
			return p.confirm({
				message: "Initialize a new git repository?",
				initialValue: true
			});
		}
	}, { onCancel: () => {
		p.cancel("Operation cancelled.");
		process.exit(0);
	} });
	return {
		...rest,
		observability: observability?.enabled,
		observabilityToken: observability?.token,
		observabilityOrgId: observability?.orgId,
		observabilityOrgName: observability?.orgName,
		skills: configureMastraToolingForAgents?.skills,
		mcpServer: configureMastraToolingForAgents?.mcpServer
	};
};
/**
* Check if the current directory has a package.json file. If not, we should alert the user to create one or run "mastra create" to create a new project. The package.json file is required to install dependencies in the next steps.
*/
const checkForPkgJson = async () => {
	const cwd = process.cwd();
	const pkgJsonPath = path.join(cwd, "package.json");
	try {
		await fsPromises.access(pkgJsonPath);
	} catch {
		p.log.error("No package.json file found in the current directory. Please run \"npm init -y\" to create one, or run \"npx create-mastra@latest\" to create a new Mastra project.");
		process.exit(1);
	}
};
/**
* Read the `name` field from the project's `package.json`, returning `undefined`
* if the file is missing or unparseable.
*/
const readPackageName = async () => {
	try {
		const raw = await fsPromises.readFile(path.join(process.cwd(), "package.json"), "utf8");
		const parsed = JSON.parse(raw);
		return typeof parsed.name === "string" && parsed.name.trim().length > 0 ? parsed.name : void 0;
	} catch {
		return;
	}
};
/**
* Generate content for AGENTS.md file
*/
function generateAgentsMarkdown({ skills, mcpServer }) {
	const hasSkills = skills && skills.length > 0;
	const hasMcp = !!mcpServer;
	let content = `# AGENTS.md
`;
	if (hasSkills) content += `
## CRITICAL: Load \`mastra\` skill first

Load the \`mastra\` skill BEFORE any Mastra work. Never rely on cached knowledge — APIs change between versions.
`;
	content += `
## Rules

- Register all agents, tools, workflows, and scorers in \`src/mastra/index.ts\`
- Use the \`dev\` and \`build\` scripts from \`package.json\` instead of running \`mastra dev\` / \`mastra build\` directly
`;
	if (hasMcp) {
		const editorName = mcpServer === "cursor-global" ? "Cursor (global)" : mcpServer.charAt(0).toUpperCase() + mcpServer.slice(1);
		content += `## MCP Docs Server

Mastra MCP Docs Server is configured for ${editorName}. Restart your editor to load it.
`;
	}
	content += `
## Resources

- [Mastra Documentation](https://mastra.ai/llms.txt)
- [Skills Discovery](https://mastra.ai/.well-known/skills/index.json)
`;
	return content;
}
/**
* Write AGENTS.md file to project root
*/
async function writeAgentsMarkdown(options) {
	const content = generateAgentsMarkdown(options);
	const filePath = path.join(process.cwd(), "AGENTS.md");
	await fsPromises.writeFile(filePath, content);
}
/**
* Write CLAUDE.md file to project root
*/
async function writeClaudeMarkdown() {
	const filePath = path.join(process.cwd(), "CLAUDE.md");
	await fsPromises.writeFile(filePath, "@AGENTS.md");
}
//#endregion
//#region src/commands/utils.ts
function getPackageManager() {
	const userAgent = process.env.npm_config_user_agent || "";
	const execPath = process.env.npm_execpath || "";
	if (userAgent.includes("bun")) return "bun";
	if (userAgent.includes("yarn")) return "yarn";
	if (userAgent.includes("pnpm")) return "pnpm";
	if (userAgent.includes("npm")) return "npm";
	if (execPath.includes("bun")) return "bun";
	if (execPath.includes("yarn")) return "yarn";
	if (execPath.includes("pnpm")) return "pnpm";
	if (execPath.includes("npm")) return "npm";
	return "npm";
}
/**
* Wrap an async commander action so failures print a clean error message
* (never a stack trace) and exit non-zero.
*/
function wrapAction(fn) {
	return (...args) => {
		fn(...args).catch((err) => {
			console.error(`Error: ${err.message}`);
			process.exit(1);
		});
	};
}
function parseMcp(value) {
	if (!isValidEditor(value)) throw new InvalidArgumentError(`Choose a valid value: ${EDITOR.join(", ")}`);
	return value;
}
function parseComponents(value) {
	const parsedValue = value.split(",");
	if (!areValidComponents(parsedValue)) throw new InvalidArgumentError(`Choose valid components: ${COMPONENTS.join(", ")}`);
	return parsedValue;
}
function parseLlmProvider(value) {
	if (!isValidLLMProvider(value)) throw new InvalidArgumentError(`Choose a valid provider: ${LLMProvider.join(", ")}`);
	return value;
}
function shouldSkipDotenvLoading() {
	return process.env.MASTRA_SKIP_DOTENV === "true" || process.env.MASTRA_SKIP_DOTENV === "1";
}
/**
* Get the version tag (e.g., 'beta', 'latest') for the currently running mastra CLI.
* Create passes its known version to avoid resolving package metadata from an installed layout.
* Init omits it and preserves the existing best-effort undefined fallback.
*/
async function getVersionTag(version) {
	try {
		let currentVersion = version;
		if (!currentVersion) {
			const pkgPath = fileURLToPath(import.meta.resolve("mastra/package.json"));
			currentVersion = (await fsExtra.readJSON(pkgPath)).version;
		}
		if (!currentVersion) throw new Error("Missing mastra package version");
		const { stdout } = await execa("npm", [
			"dist-tag",
			"ls",
			"mastra"
		], { cwd: import.meta.dirname });
		const tag = selectMatchingDistTag(currentVersion, stdout);
		if (tag) return tag;
	} catch {}
	if (version) {
		console.error("We could not resolve the mastra version tag, falling back to \"latest\"");
		return "latest";
	}
}
/**
* Check if the current directory already has git initialized.
*/
async function isGitInitialized({ cwd }) {
	try {
		await execa("git", ["rev-parse", "--is-inside-work-tree"], {
			cwd,
			stdio: "ignore"
		});
		return true;
	} catch {
		return false;
	}
}
/**
* Initialize a git repository in the specified directory.
*/
async function gitInit({ cwd }) {
	await execa("git", ["init"], {
		cwd,
		stdio: "ignore"
	});
	await execa("git", ["add", "-A"], {
		cwd,
		stdio: "ignore"
	});
	await execa("git", [
		"commit",
		"-m",
		"Initial commit from Mastra",
		"--author=\"dane-ai-mastra[bot] <dane-ai-mastra[bot]@users.noreply.github.com>\""
	], {
		cwd,
		stdio: "ignore"
	});
}
//#endregion
//#region src/analytics/index.ts
const ANALYTICS_CONFIG_PATH = path.join(os.homedir(), ".mastra", "analytics.json");
/**
* Bucket the Mastra platform API host into coarse labels for analytics.
*
* We report the *category* of API surface a deploy is talking to (cloud,
* staging, localhost, custom, unknown) rather than the raw host, so
* self-hosted or internal domains are never leaked to telemetry.
*/
function bucketApiHost(apiUrl) {
	try {
		const { host, hostname } = new URL(apiUrl);
		if (hostname === "localhost" || hostname === "127.0.0.1") return "localhost";
		if (host === "staging.mastra.cloud" || host.endsWith(".staging.mastra.cloud")) return "staging";
		if (host === "platform.mastra.ai" || host === "mastra.cloud" || host.endsWith(".mastra.cloud")) return "cloud";
		return "custom";
	} catch {
		return "unknown";
	}
}
let analyticsInstance = null;
function getAnalytics() {
	return analyticsInstance;
}
function setAnalytics(instance) {
	analyticsInstance = instance;
}
var PosthogAnalytics = class PosthogAnalytics {
	sessionId = "";
	client;
	distinctId = "";
	version;
	packageManager = "";
	constructor({ version, apiKey, host = "https://app.posthog.com" }) {
		this.version = version;
		if (!PosthogAnalytics.isTelemetryEnabled()) return;
		this.packageManager = getPackageManager();
		const { distinctId, sessionId } = this.getOrCreateAnalyticsConfig();
		this.distinctId = distinctId;
		this.sessionId = sessionId;
		this.initializePostHog(apiKey, host);
	}
	getOrCreateAnalyticsConfig(configPath = ANALYTICS_CONFIG_PATH) {
		try {
			if (existsSync(configPath)) {
				const { distinctId, sessionId } = JSON.parse(readFileSync(configPath, "utf-8"));
				if (distinctId && !this.isHostnameDerivedDistinctId(distinctId)) {
					const config = {
						distinctId,
						sessionId: sessionId || randomUUID()
					};
					if (config.sessionId !== sessionId) this.writeCliConfig(config, configPath);
					return config;
				}
			}
		} catch {}
		const config = {
			distinctId: this.createDistinctId(),
			sessionId: randomUUID()
		};
		this.writeCliConfig(config, configPath);
		return config;
	}
	writeCliConfig({ distinctId, sessionId }, configPath = ANALYTICS_CONFIG_PATH) {
		try {
			mkdirSync(path.dirname(configPath), { recursive: true });
			writeFileSync(configPath, JSON.stringify({
				distinctId,
				sessionId
			}));
		} catch {}
	}
	initializePostHog(apiKey, host) {
		this.client = new PostHog(apiKey, {
			host,
			flushAt: 1,
			flushInterval: 0,
			disableGeoip: false
		});
		this.captureSessionStart();
		process.on("exit", () => {
			this.client?.flush().catch(() => {});
		});
	}
	static isTelemetryEnabled() {
		const value = process.env.MASTRA_TELEMETRY_DISABLED;
		if (value && [
			"1",
			"true",
			"yes"
		].includes(value.trim().toLowerCase())) return false;
		return true;
	}
	createDistinctId() {
		return `mastra-${randomUUID()}`;
	}
	isHostnameDerivedDistinctId(distinctId) {
		return distinctId === `mastra-${os.hostname()}`;
	}
	getSystemProperties() {
		return {
			os: process.platform,
			os_version: os.release(),
			node_version: process.version,
			platform: process.arch,
			session_id: this.sessionId,
			cli_version: this.version || "unknown",
			machine_id: os.hostname(),
			package_manager: this.packageManager
		};
	}
	getDurationMs(startTime) {
		const [seconds, nanoseconds] = process.hrtime(startTime);
		return seconds * 1e3 + nanoseconds / 1e6;
	}
	captureSessionStart() {
		if (!this.client) return;
		this.client.capture({
			distinctId: this.distinctId,
			event: "cli_session_start",
			properties: { ...this.getSystemProperties() }
		});
	}
	getDistinctId() {
		return this.distinctId;
	}
	trackEvent(eventName, properties) {
		try {
			if (!this.client) return;
			this.client.capture({
				distinctId: this.distinctId,
				event: eventName,
				properties: {
					...this.getSystemProperties(),
					...properties
				}
			});
		} catch {}
	}
	trackCommand(options) {
		try {
			if (!this.client) return;
			const commandData = {
				command: options.command,
				status: options.status || "success"
			};
			if (options.args) commandData.args = options.args;
			if (options.durationMs) commandData.durationMs = options.durationMs;
			if (options.error) commandData.error = options.error;
			this.client.capture({
				distinctId: this.distinctId,
				event: "cli_command",
				properties: {
					...this.getSystemProperties(),
					...commandData,
					origin: options?.origin || "oss"
				}
			});
		} catch {}
	}
	async trackCommandExecution({ command, args, execution, origin }) {
		const startTime = process.hrtime();
		try {
			const result = await execution();
			const durationMs = this.getDurationMs(startTime);
			this.trackCommand({
				command,
				args,
				durationMs,
				status: "success",
				origin
			});
			return result;
		} catch (error) {
			const durationMs = this.getDurationMs(startTime);
			this.trackCommand({
				command,
				args,
				durationMs,
				status: "error",
				error: error instanceof Error ? error.message : String(error),
				origin
			});
			throw error;
		}
	}
	async shutdown(timeoutMs) {
		if (!this.client) return;
		try {
			await this.client.shutdown(timeoutMs);
		} catch {}
	}
};
//#endregion
export { DepsService as A, validateProjectName as B, writeAPIKey as C, writeIndexFile as D, writeCodeSample as E, normalizeCreateCommandOptions as F, parseCreateLLMProvider as I, parseCreateTimeout as L, CREATE_LLM_PROVIDERS as M, configureCreateCommand as N, writeObservabilityEnv as O, getCreateMode as P, selectMatchingDistTag as R, readPackageName as S, writeClaudeMarkdown as T, checkForPkgJson as _, getPackageManager as a, getAPIKey as b, isGitInitialized as c, parseMcp as d, shouldSkipDotenvLoading as f, checkAndInstallCoreDeps as g, LLMProvider as h, setAnalytics as i, installMastraDocsMCPServer as j, FileService as k, parseComponents as l, COMPONENTS as m, bucketApiHost as n, getVersionTag as o, wrapAction as p, getAnalytics as r, gitInit as s, PosthogAnalytics as t, parseLlmProvider as u, createComponentsDir as v, writeAgentsMarkdown as w, interactivePrompt as x, createMastraDir as y, validateCreateOptionConflicts as z };

//# sourceMappingURL=analytics-CeDc63iC.js.map